import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { useState, useContext, useMemo } from 'react';

import Button from '@/ui/components/Button';

import { BillingFlagReasonContext } from '../contexts/BillingFlagReasonContext';
import { BillingReportContext } from '../contexts/BillingReportProvider';
import { BillingReportRow, Reason } from '../types';
import { BillingItemFlagType, BulkIgnoreErrorsFlag } from '../types/billing_item_flag';
import { titleize } from '../utils/titleize';

import ReasonDropdown from './BillingItemFlagReasonDropdown';

interface Props {
  rows: BillingReportRow[];
  onDismiss: () => void;
  onIgnoreError: (flag: BulkIgnoreErrorsFlag) => void;
  onClearIgnoreErrors?: (
    type: string,
    onlyMyFlags: boolean,
    rows: BillingReportRow[]
  ) => Promise<void>;
}

const IgnoreErrorServiceLines = ({
  rows,
  onIgnoreError,
  onDismiss,
  onClearIgnoreErrors
}: Props): JSX.Element => {
  const [ignoreErrorReasonNote, setIgnoreErrorReasonNote] = useState('');
  const [loading, setLoading] = useState({
    submitting: false,
    clearing: false
  });
  const { setErrorMessage } = useContext(BillingReportContext);
  const [selectedReason, setSelectedReason] = useState<Reason | null>(null);
  const { billingItemFlagReasons } = useContext(BillingFlagReasonContext);
  const ignoreErrorReasons = billingItemFlagReasons['ignore_error'] || [];
  const [alertsToIgnore, setAlertsToIgnore] = useState<Record<string, string[]>>({});

  const handleOnClick = async () => {
    if (!selectedReason) {
      console.error('No selectedReason');
      return;
    }

    if (!hasSelectedAlerts()) {
      console.error('No alerts selected');

      return;
    }

    setLoading({ ...loading, submitting: true });
    try {
      await onIgnoreError({
        rows: rows,
        type: BillingItemFlagType.IGNORE_ERROR,
        reason: selectedReason.name,
        note: ignoreErrorReasonNote,
        alerts: alertsToIgnore
      });
      setTimeout(() => {
        onDismiss();
      }, 500);
    } catch (error) {
      console.error('Error ignoring service lines:', error);
      setErrorMessage('Failed to ignore the errors for the rows');
    } finally {
      setLoading({ ...loading, submitting: false });
    }
  };

  const hasSelectedAlerts = () => {
    return Object.values(alertsToIgnore).some((errors) => errors.length > 0);
  };

  const canSubmit = () => {
    return hasSelectedAlerts() && selectedReason;
  };

  const clearFlags = async (type: string, { onlyMyFlags }: { onlyMyFlags: boolean }) => {
    if (onClearIgnoreErrors) {
      setLoading({ ...loading, clearing: true });
      await onClearIgnoreErrors(type, onlyMyFlags, rows);
      setLoading({ ...loading, clearing: false });
    }
  };

  const collectedErrors = useMemo(() => {
    const errors: Record<string, string[]> = rows
      .filter((row) => Object.keys(row.errors).length > 0)

      .reduce(
        (groupedErrors, row) => {
          Object.entries(row.errors).forEach(([group, errors]) => {
            errors.forEach((error: string) => {
              if (!groupedErrors[group]) {
                groupedErrors[group] = [error];
              } else {
                if (!groupedErrors[group].includes(error)) {
                  groupedErrors[group].push(error);
                }
              }
            });
          });
          return groupedErrors;
        },
        {} as Record<string, string[]>
      );
    return errors;
  }, [rows]);

  const handleErrorCheck =
    (group: string, error: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      setAlertsToIgnore((prev) => {
        const errors = prev[group] || [];
        if (isChecked) {
          return {
            ...prev,
            [group]: [...errors, error]
          };
        } else {
          return {
            ...prev,
            [group]: [...errors.filter((e) => e !== error)]
          };
        }
      });
    };

  function handleGroupCheck(group: string): React.ChangeEventHandler<HTMLInputElement> | undefined {
    return (e) => {
      const isChecked = e.target.checked;
      setAlertsToIgnore((prev) => {
        if (isChecked) {
          return {
            ...prev,
            [group]: collectedErrors[group]
          };
        } else {
          const newErrorsToIgnore = { ...prev };
          delete newErrorsToIgnore[group];
          return newErrorsToIgnore;
        }
      });
    };
  }

  const groupIsChecked = (group: string) => {
    return (
      alertsToIgnore[group]?.length === collectedErrors[group]?.length &&
      collectedErrors[group]?.length > 0
    );
  };

  return (
    <div className="my-2 max-w-5xl bg-white p-6 rounded shadow space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl">Ignore Errors for Selected Rows</h2>
      </div>

      <div>
        {
          <p className="mt-2 text-sm text-gray-500">
            {Object.keys(collectedErrors).length === 0
              ? 'No errors found'
              : 'Select the errors you want to ignore'}
          </p>
        }
        {Object.entries(collectedErrors).map(([group, errors]) => (
          <div key={group} className="mt-2">
            <h3 className="text-lg font-semibold">
              <label>
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={groupIsChecked(group)}
                  onChange={handleGroupCheck(group)}
                />
                {titleize(group)}
              </label>
            </h3>
            <ul className="ml-4">
              {errors.map((error) => (
                <li key={error}>
                  <label>
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={alertsToIgnore[group]?.includes(error) || false}
                      onChange={handleErrorCheck(group, error)}
                    />
                    {error}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="relative my-8">
        <ReasonDropdown reasons={ignoreErrorReasons} onReasonChange={setSelectedReason} />
        {!selectedReason && (
          <p className="mt-1 text-sm text-red-500">Selecting a reason is required</p>
        )}
      </div>

      <div>
        <label htmlFor="ignore-error-reason-note" className="block text-sm font-semibold">
          Note
        </label>
        <textarea
          data-testid="ignore-error-reason-note"
          className="p-3 w-full text-sm text-gray-700 border border-gray-300 rounded focus:outline-none focus:border-blue-500 transition duration-200 ease-in-out resize-none"
          value={ignoreErrorReasonNote}
          id="ignore-error-reason-note"
          onChange={(e) => setIgnoreErrorReasonNote(e.target.value)}
          placeholder="Enter a note for ignoring the errors"
        />
      </div>

      <div className="mt-4 flex justify-between">
        <div>
          <Button className="mr-2" onClick={onDismiss} color="grey">
            Cancel
          </Button>

          <Button
            className="mr-2"
            color="red"
            onClick={() => {
              clearFlags(BillingItemFlagType.IGNORE_ERROR, {
                onlyMyFlags: false
              });
            }}
            disabled={loading.clearing}>
            {loading.clearing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Clearing...
              </>
            ) : (
              'Clear All Ignored Errors'
            )}
          </Button>

          <Button
            className="mr-2"
            color="yellow"
            onClick={() => {
              clearFlags(BillingItemFlagType.IGNORE_ERROR, {
                onlyMyFlags: true
              });
            }}
            disabled={loading.clearing}>
            {loading.clearing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Clearing...
              </>
            ) : (
              'Clear only MY Ignored Errors'
            )}
          </Button>
        </div>

        <Button
          data-testid="ignore-error-submit-btn"
          className="mr-2"
          color="green"
          disabled={!canSubmit()}
          onClick={handleOnClick}>
          {loading.submitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              Saving...
            </>
          ) : (
            'Submit Ignore Errors'
          )}
        </Button>
      </div>
    </div>
  );
};

export default IgnoreErrorServiceLines;
